import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PendingFeeDecision,
  SubmitFeeDecisionPayload,
} from '@web-app/server/api/cancellation-fees/types';

const PENDING_FEE_DECISIONS_QUERY_KEY = 'pending-fee-decisions';

const syncLateCancelCharges = async (): Promise<void> => {
  const response = await fetch('/api/cancellation-fees/sync', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    throw new Error('Failed to sync late cancel charges');
  }
};

/**
 * Fetches pending fee decisions for the current provider.
 * Calls Provider Portal API route: GET /api/cancellation-fees
 */
const fetchPendingFeeDecisions = async (): Promise<PendingFeeDecision[]> => {
  const response = await fetch('/api/cancellation-fees', {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    throw new Error('Failed to fetch pending fee decisions');
  }

  const data = (await response.json()) as { decisions: PendingFeeDecision[] };
  return data.decisions;
};

interface SubmitFeeDecisionResponse {
  success: boolean;
  autoChargeId: string;
}

/**
 * Submits a fee decision for an appointment.
 * Calls Provider Portal API route: POST /api/cancellation-fees/submit
 */
const submitFeeDecision = async (
  payload: SubmitFeeDecisionPayload,
): Promise<SubmitFeeDecisionResponse> => {
  const response = await fetch('/api/cancellation-fees/submit', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw new Error('Failed to submit fee decision');
  }

  return (await response.json()) as SubmitFeeDecisionResponse;
};

interface UseGetPendingFeeDecisionsOptions {
  enabled?: boolean;
}

/**
 * Hook to fetch all pending fee decisions for the current provider.
 * Returns appointments that need a fee handling decision (late cancel/no-show).
 */
export const useGetPendingFeeDecisions = (
  options?: UseGetPendingFeeDecisionsOptions,
) => {
  return useQuery({
    queryKey: [PENDING_FEE_DECISIONS_QUERY_KEY],
    queryFn: fetchPendingFeeDecisions,
    retry: false,
    enabled: options?.enabled ?? true,
  });
};

/**
 * Hook to submit a fee decision for a specific appointment.
 * Uses optimistic updates to immediately reflect the submitted action in the UI,
 * then syncs with the backend via query invalidation.
 */
export const useSubmitFeeDecision = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['submit-fee-decision'],
    mutationFn: submitFeeDecision,
    onMutate: async (payload) => {
      await queryClient.cancelQueries({
        queryKey: [PENDING_FEE_DECISIONS_QUERY_KEY],
      });

      const previous = queryClient.getQueryData<PendingFeeDecision[]>([
        PENDING_FEE_DECISIONS_QUERY_KEY,
      ]);

      queryClient.setQueryData<PendingFeeDecision[]>(
        [PENDING_FEE_DECISIONS_QUERY_KEY],
        (old) =>
          old?.map((d) =>
            d.appointmentInfo.appointmentId === payload.appointmentId
              ? { ...d, submittedAction: payload.action }
              : d,
          ),
      );

      return { previous };
    },
    onError: (_error, _payload, context) => {
      if (context?.previous) {
        queryClient.setQueryData(
          [PENDING_FEE_DECISIONS_QUERY_KEY],
          context.previous,
        );
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: [PENDING_FEE_DECISIONS_QUERY_KEY],
      });
    },
  });
};

/**
 * Hook to trigger a sync of late cancel charges from Admin,
 * then invalidate the pending decisions query so the UI refreshes.
 */
export const useSyncLateCancelCharges = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['sync-late-cancel-charges'],
    mutationFn: syncLateCancelCharges,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PENDING_FEE_DECISIONS_QUERY_KEY],
      });
    },
  });
};

// Re-export types for convenience
export type {
  PendingFeeDecision,
  PendingFeeDecisionAppointmentInfo,
  PendingFeeDecisionCancelReason,
  SubmitFeeDecisionPayload,
} from '@web-app/server/api/cancellation-fees/types';

// Re-export enums from their original sources
export { AppointmentStatus } from '@shared/types';
export { AppointmentCancelHandleAction } from '@web-app/utils/cancellation';
